import {
  DEFAULT_TIMEZONE,
  formatActivityLocation,
  normalizeSiteLanguage,
  pickLocalized,
} from '@yoyoclub/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import { DescriptionMarkdown } from '../components/DescriptionMarkdown';
import Card from '../components/ui/Card';
import ImageGallery from '../components/ui/ImageGallery';
import { useAuth } from '../hooks/useAuth';
import { dateLocale } from '../i18n/locale';
import { localize } from '../i18n/script';
import { passApi, type PassDetail as PassDetailType } from '../services/passApi';
import { getLocalizedTitle } from '../utils/activityTransform';

export default function PassDetail() {
  const { t, i18n } = useTranslation(['pass', 'activity', 'common']);
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  const [pass, setPass] = useState<PassDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  useEffect(() => {
    if (!slug) return;
    const fetchPass = async () => {
      try {
        setLoading(true);
        const data = await passApi.getPassBySlug(slug);
        setPass(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('pass:detail.notFound'));
      } finally {
        setLoading(false);
      }
    };
    fetchPass();
  }, [slug, t]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common:common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !pass) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('pass:detail.notFound')}</h1>
          <button
            onClick={() => navigate('/activities')}
            className="text-primary hover:underline font-medium"
          >
            {t('pass:detail.backToActivities')}
          </button>
        </div>
      </div>
    );
  }

  const passTitle = localize(pass.titleCN, pass.titleEN, i18n.language);
  const priceDollars = (pass.price / 100).toFixed(2);
  const perSessionPrice = (pass.price / pass.totalSessions / 100).toFixed(2);

  const expiresFormatted = new Date(pass.expiresAt).toLocaleDateString(dateLocale(i18n.language), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: DEFAULT_TIMEZONE,
  });

  const now = new Date();
  const isExpired = new Date(pass.expiresAt) <= now;
  const isSoldOut = pass.soldCount >= pass.maxSales;
  const isPaused = pass.status === 'PAUSED';

  const handleBuy = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: location.pathname } });
      return;
    }
    navigate(`/passes/${pass.id}/checkout`, { state: { pass } });
  };

  // Pass description in the site language, else the activity's (PRD §5.4). Raw
  // stored side; DescriptionMarkdown converts its rendered text (PRD §5.1).
  const description =
    pickLocalized(pass.descriptionCN, pass.descriptionEN, i18n.language) ||
    pickLocalized(pass.activity.descriptionCN, pass.activity.descriptionEN, i18n.language);

  const activity = pass.activity;
  const ageMin = activity.ageMin ?? 0;
  const ageMax = activity.ageMax ?? 99;
  const duration = activity.duration ?? 0;

  // Price card element (shared between mobile and desktop)
  const priceCardElement = (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sticky top-24 space-y-4">
      {/* Price */}
      <div>
        <p className="text-3xl font-bold text-primary">${priceDollars}</p>
        <p className="text-sm text-gray-500">
          {t('pass:detail.sessionsCount', { count: pass.totalSessions })} · ${perSessionPrice}/
          {t('pass:detail.perSession')}
        </p>
      </div>

      {/* Details */}
      <div className="space-y-3 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-500">{t('pass:detail.validUntil')}</span>
          <span className="font-medium text-gray-900">{expiresFormatted}</span>
        </div>
      </div>

      {/* Activity Link */}
      <div className="text-sm">
        <p className="text-gray-500 mb-1">{t('pass:detail.activity')}</p>
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/activity/${pass.activity.slug}`);
          }}
          className="text-primary hover:underline font-medium"
        >
          {getLocalizedTitle(pass.activity, i18n.language)}
        </button>
      </div>

      {/* Status & Buy Button */}
      {pass.isBuyable ? (
        <>
          <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            {t('pass:detail.available')}
          </div>
          <button
            onClick={handleBuy}
            className="w-full py-3 px-4 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-colors"
          >
            {isAuthenticated ? t('pass:detail.buyButton') : t('pass:detail.loginToBuy')}
          </button>
        </>
      ) : (
        <>
          <div
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              isSoldOut
                ? 'bg-red-100 text-red-800'
                : isExpired
                  ? 'bg-gray-100 text-gray-600'
                  : 'bg-gray-100 text-gray-600'
            }`}
          >
            {isSoldOut
              ? t('pass:detail.soldOut')
              : isExpired
                ? t('pass:detail.expired')
                : isPaused
                  ? t('pass:detail.paused')
                  : t('pass:detail.paused')}
          </div>
          <button
            disabled
            className="w-full py-3 px-4 bg-gray-300 text-gray-500 rounded-lg font-semibold cursor-not-allowed"
          >
            {isSoldOut
              ? t('pass:detail.soldOut')
              : isExpired
                ? t('pass:detail.expired')
                : t('pass:detail.paused')}
          </button>
        </>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container-custom py-4 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
          {/* Left Column - Details */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Image Gallery */}
            <ImageGallery images={activity.images} alt={passTitle} />

            {/* Title */}
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
                {passTitle}
              </h1>
              {pass.host && (
                <p className="text-sm text-gray-500 mt-1">
                  {t('pass:detail.hostedBy')} {pass.host.displayName}
                </p>
              )}
            </div>

            {/* Quick Info Bar */}
            <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-xs sm:text-sm text-gray-600">
              {/* Age Range */}
              <div className="flex items-center gap-2">
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
                <span>
                  {t('common:ages.label')} {ageMax >= 99 ? `${ageMin}+` : `${ageMin}-${ageMax}`}
                </span>
              </div>

              {/* Duration */}
              {duration > 0 && (
                <div className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>
                    {duration >= 60
                      ? `${Math.floor(duration / 60)}${t('common:duration.hours')}${duration % 60 > 0 ? ` ${duration % 60}${t('common:duration.minutes')}` : ''}`
                      : `${duration}${t('common:duration.minutes')}`}
                  </span>
                </div>
              )}

              {/* Category */}
              {activity.category && (
                <div className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                    />
                  </svg>
                  <span>{t(`common:category.${activity.category}`, activity.category)}</span>
                </div>
              )}

              {/* Location */}
              {(() => {
                const locText = formatActivityLocation({
                  region: activity.region,
                  locationDetails: activity.locationDetails,
                  fallbackLocation: activity.location,
                  locale: normalizeSiteLanguage(i18n.language),
                });
                if (!locText) return null;
                return (
                  <div className="flex items-center gap-2">
                    <svg
                      className="w-4 h-4 sm:w-5 sm:h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <span>{locText}</span>
                  </div>
                );
              })()}
            </div>

            {/* Mobile: Price Card */}
            <div className="lg:hidden">{priceCardElement}</div>

            {/* Description */}
            {description && (
              <Card padding="lg">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">
                  {t('activity:detail.about')}
                </h2>
                <div className="relative">
                  <div
                    className={`prose prose-sm max-w-none text-gray-700 prose-headings:text-base prose-headings:sm:text-lg prose-headings:font-semibold prose-p:text-sm prose-p:sm:text-base prose-p:leading-relaxed overflow-hidden transition-all duration-300 ${
                      isDescriptionExpanded ? '' : 'max-h-48 sm:max-h-none'
                    }`}
                  >
                    <DescriptionMarkdown lang={i18n.language}>{description}</DescriptionMarkdown>
                  </div>
                  {!isDescriptionExpanded && (
                    <div className="sm:hidden absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent pointer-events-none" />
                  )}
                  {!isDescriptionExpanded && (
                    <button
                      onClick={() => setIsDescriptionExpanded(true)}
                      className="sm:hidden w-full mt-2 py-2 text-primary font-medium text-sm flex items-center justify-center gap-1"
                    >
                      {t('activity:detail.showMore')}
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                  )}
                  {isDescriptionExpanded && (
                    <button
                      onClick={() => setIsDescriptionExpanded(false)}
                      className="sm:hidden w-full mt-2 py-2 text-primary font-medium text-sm flex items-center justify-center gap-1"
                    >
                      {t('activity:detail.showLess')}
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 15l7-7 7 7"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </Card>
            )}
          </div>

          {/* Right Column - Price Card (desktop only) */}
          <div className="hidden lg:block lg:col-span-1">{priceCardElement}</div>
        </div>
      </div>
    </div>
  );
}
